import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Modal } from '@sg/garnish';

import { GroupOrderConfirmationContent } from '../../../GroupOrderConfirmationContent';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderBagCancelModal = (props: Props) => {
  const { visible, isCancelingGroupOrder, onClose, onConfirm } = props;

  const { formatMessage } = useIntl();

  // ─── Handlers ───────────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isCancelingGroupOrder) return;

    onClose();
  }, [isCancelingGroupOrder, onClose]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={visible}
      size="small"
      withSafeAreaView={false}
      accessibilityLabel={formatMessage(messages.accessibilityLabel)}
      onRequestClose={handleClose}
    >
      <GroupOrderConfirmationContent
        title={<FormattedMessage {...messages.title} />}
        body={<FormattedMessage {...messages.body} />}
        dismissButtonLabel={<FormattedMessage {...messages.goBackCta} />}
        confirmButtonLabel={<FormattedMessage {...messages.cancelGroupOrderCta} />}
        confirmButtonPalette="caution"
        isPerformingAction={isCancelingGroupOrder}
        onClose={handleClose}
        onConfirm={onConfirm}
      />
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accessibilityLabel: {
    defaultMessage: 'Cancel group order confirmation',
    description: 'Bag | Group order | Cancel modal | Accessibility label',
  },
  title: {
    defaultMessage: 'Cancel group order?',
    description: 'Bag | Group order | Cancel modal | Title',
  },
  body: {
    defaultMessage:
      'This will cancel the order for everyone in the group. They’ll all be notified.',
    description: 'Bag | Group order | Cancel modal | Body',
  },
  goBackCta: {
    defaultMessage: 'Go back',
    description: 'Bag | Group order | Cancel modal | Go back CTA',
  },
  cancelGroupOrderCta: {
    defaultMessage: 'Cancel group order',
    description: 'Bag | Group order | Cancel modal | Confirm CTA',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  visible: boolean;
  isCancelingGroupOrder: boolean;
  onClose: () => void;
  onConfirm: () => void;
};
